'use client';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Send, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { submitMessage } from '@/app/actions/submitContactMessage';
import type { ContactMessage } from '@/types/types';
import PhoneNumberInput from '../ui/custom/PhoneNumberInput';
import { FormField } from '@/components/forms/FormField';
import { contactFormSchema, type ContactFormData } from '@/lib/validations';

const MAX_MESSAGE_LENGTH = 750;

const baseInputClass =
  'flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ' +
  'ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none ' +
  'focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm transition-colors';

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset: resetForm,
    control,
    formState: { errors, isValid, dirtyFields },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      subject: 'general',
      message: '',
    },
  });

  const messageValue = watch('message', '');

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      await submitMessage({ authToken: null, data: { data: data as ContactMessage } });
      setSubmitSuccess(true);
      resetForm();
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/50">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="py-2 text-sm font-medium text-orange-700 mb-3 uppercase">We&apos;d Love to Hear From You</div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Send us a Message</h3>
        <p className="text-sm text-gray-600">
          Need to place an order? Check out our{' '}
          <Link
            href="/menu"
            className="text-orange-600 hover:text-orange-700 font-medium decoration-orange-200 hover:decoration-orange-400 transition-colors">
            menu page
          </Link>{' '}
          for easy ordering.
        </p>
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        {/* Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField name="name" control={control} error={errors.name} required>
            <FormField.Label>Your Name</FormField.Label>
            <FormField.Input type="text" placeholder="Provide your name" />
            <FormField.Error />
          </FormField>

          {/* Email */}
          <FormField name="email" control={control} error={errors.email} required>
            <FormField.Label>Email</FormField.Label>
            <FormField.Input type="email" placeholder="Provide your email" />
            <FormField.Error />
          </FormField>

          {/* Phone */}
          <FormField name="phone" control={control} error={errors.phone}>
            <FormField.Label>Your Phone</FormField.Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneNumberInput
                  id="phone"
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.phone}
                  success={dirtyFields.phone && !errors.phone}
                  placeholder="Optional - for faster contact"
                />
              )}
            />
            <FormField.Error />
          </FormField>

          <FormField name="subject" control={control} error={errors.subject} required>
            <FormField.Label>How Can We Help?</FormField.Label>
            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger id="subject" className={errors.subject ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Question</SelectItem>
                    <SelectItem value="catering">Catering Inquiry</SelectItem>
                    <SelectItem value="order-issue">Issue with an Order</SelectItem>
                    <SelectItem value="menu-request">Request for Menu or Nutritional Info</SelectItem>
                    <SelectItem value="event-booking">Event Booking</SelectItem>
                    <SelectItem value="bulk-order">Bulk or Corporate Order</SelectItem>
                    <SelectItem value="feedback">Share Feedback</SelectItem>
                    <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                    <SelectItem value="career">Job or Career Inquiry</SelectItem>
                    <SelectItem value="other">Something Else</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <FormField.Error />
          </FormField>
        </div>

        {/* Message */}
        <FormField name="message" control={control} error={errors.message} required>
          <FormField.Label>Your Message</FormField.Label>
          <FormField.Description>Tell us how we can help you</FormField.Description>
          <div className="relative">
            <textarea
              id="message"
              rows={4}
              placeholder="Hi there! I'd love to know more about..."
              {...register('message')}
              className={`${baseInputClass} resize-none ${errors.message ? 'border-red-500' : ''}`}
            />
            <div className="absolute bottom-2 right-2">
              <span
                className={`text-xs ${
                  messageValue.length > MAX_MESSAGE_LENGTH
                    ? 'text-red-500'
                    : messageValue.length > MAX_MESSAGE_LENGTH * 0.9
                      ? 'text-yellow-500'
                      : 'text-gray-400'
                }`}>
                {messageValue.length}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
          </div>
          <FormField.Error />
        </FormField>

        {/* Success Message */}
        {submitSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-orange-600">
              <span className="font-medium">Thank you for reaching out!</span>
            </div>
            <p className="text-sm text-orange-600 mt-1">We&apos;ll get back to you as soon as possible.</p>
          </motion.div>
        )}

        {/* Submit Button */}
        <div className="pt-2">
          <motion.button
            type="submit"
            disabled={isSubmitting || !isValid}
            whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
            whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
            className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white py-3 px-6 rounded-xl text-sm font-semibold hover:from-orange-700 hover:to-amber-700 transition-all duration-300 shadow-md hover:shadow-lg group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md">
            <span className="flex items-center justify-center gap-2">
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              )}
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </span>
          </motion.button>

          <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-500">
            <span>We typically respond within 24 hours</span>
          </div>
        </div>
      </div>
    </form>
  );
}
